<template>
    <div v-if="isVisibleNews" class="modal-overlay">
      <div class="modal-content">
        <h3 class="title">Nueva Noticia</h3>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label for="title">Titulo</label>
            <input type="text" id="title" v-model="title" required />
          </div>
  
          <div class="form-group">
            <label for="year">Año</label>
            <input
              type="number"
              id="year"
              v-model="year"
              placeholder="Año"
              min="2000"
              max="2100"
              required
            />
          </div>
  
          <!-- Editor Quill Corregido -->
          <div class="form-group">
            <label>Descripción</label>
            <div class="quill-editor-wrapper">
              <div ref="quillToolbar" class="quill-toolbar">
                <span class="ql-formats">
                  <button class="ql-bold" type="button"></button>
                  <button class="ql-italic" type="button"></button>
                  <button class="ql-underline" type="button"></button>
                </span>
                <span class="ql-formats">
                  <button class="ql-link" type="button"></button>
                  <button class="ql-list" value="ordered" type="button"></button>
                  <button class="ql-list" value="bullet" type="button"></button>
                </span>
              </div>
              <div ref="quillEditor" class="quill-editor-container"></div>
            </div>
          </div>
  
          <div style="display: flex; justify-content: flex-end;">
            <button type="submit" :disabled="!title || !description || !year">Enviar</button>
            <button type="button" @click="closeModal">Cerrar</button>
          </div>
        </form>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted, nextTick, watch, defineComponent } from "vue";
  import Quill from "quill";
  import "quill/dist/quill.snow.css";
  import { showSuccessToast } from "@/kernel/alerts";
  import { createNews } from "../services/news-service";
  
  export default defineComponent({
    props: {
      isVisibleNews: {
        type: Boolean,
        required: true,
      },
    },
    emits: ["update:visible", "update:news"],
    setup(props, { emit }) {
      // Campos del formulario
      const title = ref("");
      const year = ref("");
      const description = ref("");
  
      // Refs del DOM
      const quillEditor = ref(null);
      const quillToolbar = ref(null);
  
      // Instancia de Quill
      const quill2 = ref(null);
  
      // Inicializar Quill
      const initializeQuill = async () => {
        if (quill2.value || !quillEditor.value || !quillToolbar.value) return;
  
        await nextTick();
  
        try {
          quill2.value = new Quill(quillEditor.value, {
            modules: {
              toolbar: quillToolbar.value,
            },
            theme: "snow",
            placeholder: "Escribe tu contenido aquí...",
          });
  
          // Escuchar cambios en el contenido
          quill2.value.on("text-change", () => {
            description.value = quill2.value.root.innerHTML;
          });
        } catch (error) {
          console.error("Error inicializando Quill:", error);
        }
      };
  
      // Limpiar Quill
      const destroyQuill = () => {
        if (quill2.value) {
          quill2.value = null;
        }
      };
  
      // Cerrar modal
      const closeModal = () => {
        emit("update:visible", false);
        resetForm();
      };
  
      // Resetear formulario
      const resetForm = () => {
        title.value = "";
        year.value = "";
        description.value = "";
        if (quill2.value) {
          quill2.value.setContents([]);
        }
      };
  
      // Enviar formulario
      const submitForm = async () => {
        const newsData = {
          title: title.value,
          description: description.value,
          year: year.value,
        };
  
        try {
          const result = await createNews(newsData);
          if (result.status === 200) {
            showSuccessToast("Noticia creada correctamente");
            emit("update:news");
            emit("update:visible", false);
            resetForm();
          }
        } catch (error) {
          console.error("Error al enviar los datos:", error);
        }
      };
  
      watch(
        () => props.isVisibleNews,
        async (newValue) => {
          if (newValue) {
            await nextTick();
            setTimeout(() => {
              initializeQuill();
            }, 100);
          } else {
            destroyQuill();
          }
        }
      );
  
      onMounted(() => {
        if (props.isVisibleNews) {
          initializeQuill();
        }
      });
  
      return {
        title,
        year,
        description,
        quillEditor,
        quillToolbar,
        closeModal,
        submitForm,
      };
    },
  });
  </script>
  
  <style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 500px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  input,
  select {
    width: 100%;
    padding: 8px;
    margin-top: 5px;
    border-radius: 4px;
    border: 1px solid #ccc;
    box-sizing: border-box;
  }
  
  button {
    padding: 10px 15px;
    margin-top: 10px;
    cursor: pointer;
    background-color: orange;
    color: white;
    border: none;
    border-radius: 16px;
  }
  
  button[type="submit"]:hover {
    background-color: #ff8c00;
  }
  
  button[type="button"] {
    margin-left: 16px;
    background-color: darkgray;
  }
  
  button[type="button"]:hover {
    background-color: gray;
  }
  
  .title {
    margin-top: 0;
    color: orange;
  }
  
  .error-message {
    color: red;
    font-size: 12px;
    margin-top: 4px;
    display: block;
  }
  
  button[disabled] {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  /* Estilos específicos para Quill */
  .quill-editor-wrapper {
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
  }
  
  .quill-toolbar {
    border-bottom: 1px solid #ccc;
    padding: 8px;
    background: #f8f9fa;
  }
  
  .quill-editor-container {
    min-height: 200px;
    max-height: 300px;
    overflow-y: auto;
  }
  
  /* Sobrescribir estilos de Quill para que funcione correctamente */
  :deep(.ql-toolbar) {
    border: none !important;
    padding: 8px !important;
    background: #f8f9fa !important;
  }
  
  :deep(.ql-container) {
    border: none !important;
    font-size: 14px !important;
  }
  
  :deep(.ql-editor) {
    min-height: 200px;
    max-height: 300px;
    overflow-y: auto;
    padding: 12px 15px;
    line-height: 1.42;
  }
  
  :deep(.ql-editor.ql-blank::before) {
    color: #aaa;
    content: attr(data-placeholder);
    font-style: italic;
    pointer-events: none;
    position: absolute;
  }
  
  /* Estilos para botones de la toolbar */
  :deep(.ql-formats) {
    margin-right: 15px;
  }
  
  :deep(.ql-toolbar button) {
    background: none !important;
    border: none !important;
    color: #444 !important;
    padding: 5px !important;
    margin: 0 2px !important;
    border-radius: 3px !important;
  }
  
  :deep(.ql-toolbar button:hover) {
    background: #e6e6e6 !important;
  }
  
  :deep(.ql-toolbar button.ql-active) {
    background: #ccc !important;
    color: #000 !important;
  }
  </style>